import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Camera, ChevronLeft, ChevronRight, Expand, X } from "lucide-react";

/**
 * Premium Emerald Honours event gallery: feature photograph + 2x2 supporting grid,
 * draggable filmstrip and an accessible lightbox. Items are CMS-driven.
 */
export function EmeraldEventGallery({ items = [], credit = "T. Glinton Photography", year = "2026" }) {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const stripRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const touch = useRef(0);

  const total = items.length;
  const go = useCallback((next) => setActive((cur) => (total ? (next + total) % total : 0)), [total]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") go(active + 1);
      if (e.key === "ArrowLeft") go(active - 1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, active, go]);

  useEffect(() => {
    const strip = stripRef.current;
    const chip = strip?.querySelector(`[data-strip-index="${active}"]`);
    if (chip) chip.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", inline: "center", block: "nearest" });
  }, [active, reduceMotion]);

  if (!total) return null;

  const feature = items[active];
  const supporting = Array.from({ length: Math.min(4, Math.max(total - 1, 0)) }, (_, i) => {
    const index = (active + i + 1) % total;
    return { ...items[index], index };
  });

  const onPointerDown = (e) => {
    drag.current = {
      down: true,
      startX: e.clientX,
      startScroll: stripRef.current?.scrollLeft || 0,
      moved: false,
    };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down || !stripRef.current) return;
    const delta = e.clientX - drag.current.startX;
    if (Math.abs(delta) > 4) drag.current.moved = true;
    stripRef.current.scrollLeft = drag.current.startScroll - delta;
  };
  const endDrag = () => {
    drag.current.down = false;
  };

  return (
    <section className="eh-night" id="event-gallery" aria-labelledby="eh-night-title">
      <div className="eh-night__glow" aria-hidden="true" />

      <div className="bka-container">
        <div className="eh-night__head">
          <div>
            <p className="eh-night__overline">Emerald Honours</p>
            <h2 id="eh-night-title" className="eh-night__title">
              A Night to <span>Remember</span>
            </h2>
            <p className="eh-night__year">Emerald Honours {year}</p>
          </div>
          <p className="eh-night__lede">
            Celebrating extraordinary people, powerful stories and an unforgettable evening of
            recognition.
          </p>
        </div>

        <div className="eh-night__stage">
          <div className="eh-night__feature">
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={feature.src}
                src={feature.src}
                alt={feature.alt}
                initial={reduceMotion ? false : { opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.99 }}
                transition={{ duration: reduceMotion ? 0.15 : 0.75, ease: [0.22, 1, 0.36, 1] }}
              />
            </AnimatePresence>

            <button
              type="button"
              className="eh-night__open"
              onClick={() => setLightbox(true)}
              aria-label="Open photograph in full screen"
              data-testid="eh-gallery-open-lightbox"
            >
              <span className="eh-night__open-hit" />
              <span className="eh-night__open-badge">
                <Expand className="h-4 w-4" aria-hidden="true" /> View larger
              </span>
            </button>

            <button
              type="button"
              className="eh-night__nav eh-night__nav--prev"
              onClick={() => go(active - 1)}
              aria-label="Previous photograph"
              data-testid="eh-gallery-prev"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="eh-night__nav eh-night__nav--next"
              onClick={() => go(active + 1)}
              aria-label="Next photograph"
              data-testid="eh-gallery-next"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>

            {feature.caption && <p className="eh-night__caption">{feature.caption}</p>}
          </div>

          <div className="eh-night__grid">
            {supporting.map((item) => (
              <button
                type="button"
                key={`${item.src}-${item.index}`}
                className="eh-night__tile"
                onClick={() => setActive(item.index)}
                aria-label={`Show photograph: ${item.alt}`}
                data-testid={`eh-gallery-tile-${item.index}`}
              >
                <img src={item.thumb || item.src} alt={item.alt} loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        <div className="eh-night__strip-wrap">
          <button
            type="button"
            className="eh-night__strip-nav"
            onClick={() => go(active - 1)}
            aria-label="Scroll filmstrip back"
            data-testid="eh-gallery-strip-prev"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>

          <div
            ref={stripRef}
            className="eh-night__strip"
            role="list"
            aria-label="Emerald Honours event photographs"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            data-testid="eh-gallery-filmstrip"
          >
            {items.map((item, index) => (
              <button
                type="button"
                role="listitem"
                key={`${item.src}-strip-${index}`}
                data-strip-index={index}
                className={`eh-night__chip ${index === active ? "is-active" : ""}`}
                aria-pressed={index === active}
                aria-label={`Show photograph ${index + 1} of ${total}`}
                onClick={() => {
                  if (!drag.current.moved) setActive(index);
                }}
                data-testid={`eh-gallery-strip-${index}`}
              >
                <img src={item.thumb || item.src} alt="" loading="lazy" />
              </button>
            ))}
          </div>

          <button
            type="button"
            className="eh-night__strip-nav"
            onClick={() => go(active + 1)}
            aria-label="Scroll filmstrip forward"
            data-testid="eh-gallery-strip-next"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <p className="eh-night__credit">
          <Camera className="h-3.5 w-3.5" aria-hidden="true" />
          Photography: {credit}
        </p>
      </div>

      {createPortal(
        <AnimatePresence>
          {lightbox && (
          <motion.div
            className="eh-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Emerald Honours photograph viewer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onTouchStart={(e) => {
              touch.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              const delta = e.changedTouches[0].clientX - touch.current;
              if (delta > 50) go(active - 1);
              if (delta < -50) go(active + 1);
            }}
            data-testid="eh-gallery-lightbox"
          >
            <button
              type="button"
              className="eh-lightbox__close"
              onClick={() => setLightbox(false)}
              aria-label="Close photograph viewer"
              data-testid="eh-gallery-lightbox-close"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>

            <button
              type="button"
              className="eh-lightbox__nav eh-lightbox__nav--prev"
              onClick={() => go(active - 1)}
              aria-label="Previous photograph"
              data-testid="eh-gallery-lightbox-prev"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </button>

            <figure className="eh-lightbox__figure">
              <img src={feature.src} alt={feature.alt} />
              <figcaption>
                {feature.caption || feature.alt}
                <span>
                  {active + 1} / {total} · Photography: {credit}
                </span>
              </figcaption>
            </figure>

            <button
              type="button"
              className="eh-lightbox__nav eh-lightbox__nav--next"
              onClick={() => go(active + 1)}
              aria-label="Next photograph"
              data-testid="eh-gallery-lightbox-next"
            >
              <ChevronRight className="h-6 w-6" aria-hidden="true" />
            </button>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
}

export default EmeraldEventGallery;
